import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { subscribePush, unsubscribePush } from "@/lib/push";
import { Save, Bell, BellOff, Lock, Eye, EyeOff, Loader2, Mail, MailX } from "lucide-react";

export default function Profilo() {
  const { user, refresh } = useAuth();
  const isClient = user?.role === "cliente";
  const [full_name, setFullName] = useState(user?.full_name || "");
  const [company_name, setCompanyName] = useState(user?.company_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [notif, setNotif] = useState(user?.notification_enabled ?? true);
  const [emailNotif, setEmailNotif] = useState(user?.email_enabled ?? true);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/profile", { full_name, company_name, phone, notification_enabled: notif, email_enabled: emailNotif });
      if (notif) await subscribePush();
      else await unsubscribePush();
      await refresh();
      toast.success("Profilo aggiornato");
    } catch (e) {
      toast.error("Errore aggiornamento");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Profilo</h1>
        <p className="text-sm text-slate-500 mt-1">Dati account e preferenze notifiche.</p>
      </div>

      <div className="card-elevated p-6">
        <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
          <div className="w-16 h-16 rounded-2xl brand-gradient flex items-center justify-center text-white font-display font-bold text-2xl">
            {user?.full_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            <div className="font-display font-bold text-lg">{user?.full_name}</div>
            <div className="text-sm text-slate-500">{user?.email}</div>
            <div className="mt-1 inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700">{user?.role}</div>
          </div>
        </div>

        {isClient && (
          <div className="mt-5 mb-4 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-xs text-blue-900">
            I dati anagrafici (nome, ragione sociale, telefono, email) sono gestiti dal team Adybo. Per modifiche contatta{" "}
            <a href="mailto:direzione@adybo.it" className="font-semibold underline">direzione@adybo.it</a>.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <Field label="Nome referente" value={full_name} onChange={setFullName} testid="profile-name" readOnly={isClient} />
          <Field label="Ragione sociale" value={company_name} onChange={setCompanyName} testid="profile-company" readOnly={isClient} />
          <Field label="Telefono" value={phone} onChange={setPhone} testid="profile-phone" readOnly={isClient} />
          <Field label="Email" value={user?.email || ""} onChange={() => {}} testid="profile-email" readOnly />
        </div>

        <div className="mt-6 pt-5 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                {notif ? <Bell size={16} /> : <BellOff size={16} />} Notifiche push
              </div>
              <div className="text-xs text-slate-500 mt-1">Ricevi una notifica del browser/PWA quando pubblichiamo nuovi prezzi, report o news. Puoi disattivarle in qualsiasi momento.</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="notif-toggle">
              <input type="checkbox" checked={notif} onChange={(e) => setNotif(e.target.checked)} className="sr-only peer" />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer-checked:bg-blue-600 transition"></div>
              <div className="absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition peer-checked:translate-x-5 shadow"></div>
            </label>
          </div>
          <div className="text-[11px] text-slate-400 mt-3">
            Nota: se disattivi le notifiche push, le riceverai comunque dentro il portale (campanella in alto e pagina Notifiche).
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                {emailNotif ? <Mail size={16} /> : <MailX size={16} />} Ricevi email
              </div>
              <div className="text-xs text-slate-500 mt-1">Ricevi via email gli aggiornamenti importanti (nuovi report, news pubblicate, scadenze). Puoi disattivarle in qualsiasi momento senza perdere l'accesso al portale.</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="email-toggle">
              <input type="checkbox" checked={emailNotif} onChange={(e) => setEmailNotif(e.target.checked)} className="sr-only peer" />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer-checked:bg-blue-600 transition"></div>
              <div className="absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition peer-checked:translate-x-5 shadow"></div>
            </label>
          </div>
          <div className="text-[11px] text-slate-400 mt-3">
            Le comunicazioni di servizio (es. reset password, sicurezza account) verranno sempre inviate anche se disattivi le email.
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-slate-100 text-xs text-slate-500 space-y-1">
          <div>Data registrazione: {fmtDateTime(user?.created_at)}</div>
          <div>Ultimo accesso: {fmtDateTime(user?.last_login_at)}</div>
        </div>

        <div className="mt-6">
          <button disabled={busy} onClick={save} className="btn-brand flex items-center gap-2" data-testid="profile-save">
            <Save size={16} /> Salva modifiche
          </button>
        </div>
      </div>

      {user?.is_demo ? (
        <div className="card-elevated p-5 border border-amber-200 bg-amber-50/40" data-testid="demo-notice">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <span className="text-amber-700 font-bold text-sm">DEMO</span>
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-slate-900">Account demo</div>
              <div className="text-sm text-slate-600 mt-1">
                Questo è un account dimostrativo. Per motivi di sicurezza non è possibile modificare la password autonomamente.
                Se hai bisogno di credenziali personali per l'accesso continuativo, contatta il tuo referente Adybo.
              </div>
              {user.demo_expires_at && (
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Scadenza:</span>
                  <span className="font-semibold text-slate-800" data-testid="demo-expires-at">
                    {new Date(user.demo_expires_at).toLocaleDateString("it-IT")}
                  </span>
                  {typeof user.demo_days_left === "number" && (
                    <span
                      className={`px-2 py-0.5 rounded-full font-bold ${
                        user.demo_days_left < 0
                          ? "bg-red-100 text-red-700"
                          : user.demo_days_left <= 3
                          ? "bg-orange-100 text-orange-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                      data-testid="demo-days-left"
                    >
                      {user.demo_days_left < 0
                        ? `Scaduto da ${Math.abs(user.demo_days_left)} giorni`
                        : user.demo_days_left === 0
                        ? "Scade oggi"
                        : `${user.demo_days_left} giorni rimanenti`}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <ChangePasswordCard />
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, readOnly }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className={`w-full px-4 py-2.5 rounded-lg border text-sm outline-none ${readOnly ? "bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed" : "border-slate-200 focus:border-blue-500"}`}
        data-testid={testid}
      />
    </div>
  );
}

function computePasswordStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "bg-slate-200", text: "", checks: {} };
  const checks = {
    length8: pw.length >= 8,
    length12: pw.length >= 12,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  let score = 0;
  if (checks.length8) score++;
  if (checks.length12) score++;
  if (checks.lower && checks.upper) score++;
  if (checks.digit) score++;
  if (checks.symbol) score++;
  // Cap 4 buckets (0-4)
  const bucket = Math.min(4, score);
  if (bucket <= 1) return { score: 1, label: "Debole", color: "bg-red-500", text: "text-red-600", checks };
  if (bucket === 2) return { score: 2, label: "Media", color: "bg-amber-500", text: "text-amber-600", checks };
  if (bucket === 3) return { score: 3, label: "Buona", color: "bg-blue-500", text: "text-blue-600", checks };
  return { score: 4, label: "Forte", color: "bg-emerald-500", text: "text-emerald-600", checks };
}

function PasswordStrengthMeter({ password }) {
  const s = computePasswordStrength(password);
  if (!password) return null;
  return (
    <div className="mt-2" data-testid="pw-strength">
      <div className="flex items-center gap-2">
        <div className="flex-1 flex gap-1">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= s.score ? s.color : "bg-slate-200"
              }`}
              data-testid={`pw-strength-bar-${i}`}
            />
          ))}
        </div>
        <span className={`text-xs font-semibold ${s.text}`} data-testid="pw-strength-label">
          {s.label}
        </span>
      </div>
      <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
        <li className={s.checks.length8 ? "text-emerald-600" : ""}>
          {s.checks.length8 ? "✓" : "•"} Almeno 8 caratteri
        </li>
        <li className={s.checks.length12 ? "text-emerald-600" : ""}>
          {s.checks.length12 ? "✓" : "•"} 12+ caratteri (consigliato)
        </li>
        <li className={s.checks.lower && s.checks.upper ? "text-emerald-600" : ""}>
          {s.checks.lower && s.checks.upper ? "✓" : "•"} Maiuscole e minuscole
        </li>
        <li className={s.checks.digit ? "text-emerald-600" : ""}>
          {s.checks.digit ? "✓" : "•"} Almeno un numero
        </li>
        <li className={s.checks.symbol ? "text-emerald-600" : ""}>
          {s.checks.symbol ? "✓" : "•"} Almeno un simbolo
        </li>
      </ul>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const strength = computePasswordStrength(next);

  const submit = async () => {
    if (!current) return toast.error("Inserisci la password attuale");
    if (next.length < 8) return toast.error("La nuova password deve essere di almeno 8 caratteri");
    if (strength.score < 2) return toast.error("La password è troppo debole. Aggiungi maiuscole, numeri o simboli.");
    if (next !== confirm) return toast.error("Le due password non coincidono");
    if (next === current) return toast.error("La nuova password deve essere diversa da quella attuale");
    setBusy(true);
    try {
      await api.post("/profile/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata correttamente");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento password");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-4 py-2.5 pr-11 rounded-lg border border-slate-200 focus:border-blue-500 outline-none text-sm";

  return (
    <div className="card-elevated p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-1">
        <Lock size={18} className="text-blue-600" />
        <h2 className="font-display font-bold text-lg text-slate-900">Cambio password</h2>
      </div>
      <p className="text-xs text-slate-500 mb-5">
        Per motivi di sicurezza scegli una password nuova di almeno 8 caratteri, diversa da quella attuale.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Password attuale</label>
          <div className="relative">
            <input
              type={showCur ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className={inputCls}
              data-testid="pw-current"
            />
            <button type="button" onClick={() => setShowCur((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700" aria-label={showCur ? "Nascondi" : "Mostra"}>
              {showCur ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Nuova password</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                className={inputCls}
                data-testid="pw-new"
              />
              <button type="button" onClick={() => setShowNew((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700" aria-label={showNew ? "Nascondi" : "Mostra"}>
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <PasswordStrengthMeter password={next} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Conferma nuova password</label>
            <input
              type={showNew ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none text-sm"
              data-testid="pw-confirm"
            />
          </div>
        </div>

        {next && next.length < 8 && (
          <div className="text-xs text-amber-600">La nuova password deve essere di almeno 8 caratteri.</div>
        )}
        {confirm && next && confirm !== next && (
          <div className="text-xs text-red-600">Le due password non coincidono.</div>
        )}

        <div>
          <button
            disabled={busy || !current || !next || !confirm}
            onClick={submit}
            className="btn-brand flex items-center gap-2 disabled:opacity-40"
            data-testid="pw-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            Aggiorna password
          </button>
        </div>
      </div>
    </div>
  );
}
